/*
 * Waveline canvas animation for the HeroInTune component.
 *
 * A sus4 chord drawn as three pure sine voices sharing one centre
 * line, so they braid around each other:
 *
 *   tonic          f          6 units   hot pink
 *   perfect 4th    f × 4/3    8 units   soft pink
 *   perfect 5th    f × 3/2    9 units   cyan
 *
 * Both the spatial wavelengths and the drift speeds sit in the
 * just-intonation ratio 6 : 8 : 9, so the braid is truly periodic —
 * the whole figure returns to its exact starting shape every 6
 * tonic cycles, like a chord held to the bar. Where two voices
 * cross in unison the particles brighten: consonance made visible.
 */

type VoiceName = 'tonic' | 'fourth' | 'fifth'

interface Voice {
  name: VoiceName
  /** Frequency ratio relative to the tonic */
  ratio: number
  /** Wave amplitude as a fraction of the canvas height */
  amp: number
  /** Base particle brightness for this voice */
  weight: number
  /** Drift direction along the x axis */
  dir: 1 | -1
}

interface Particle {
  voice: Voice
  x: number
  size: number
  /** Twinkle phase offset so particles do not pulse in step */
  twinklePhase: number
}

/* Milliseconds for the tonic to drift one full cycle */
const T_TONIC = 16000

/* Tonic wavelengths across the canvas width */
const CYCLES = 2.0

/* Shared centre line, as a fraction of the canvas height */
const CENTRE = 0.55

/* How close two voices must be, in px, to "ring" with a unison glow */
const UNISON = 26

const VOICES: Voice[] = [
  { name: 'tonic', ratio: 1, amp: 0.07, weight: 1.0, dir: 1 },
  { name: 'fourth', ratio: 4 / 3, amp: 0.052, weight: 0.72, dir: -1 },
  { name: 'fifth', ratio: 3 / 2, amp: 0.058, weight: 0.8, dir: 1 },
]

/*
 * Sprite and guide-line colours mirror the palette tokens
 * (hot-pink, soft-pink, deep-pink, cyan) from root.css. Canvas
 * drawing cannot resolve CSS light-dark() values, so the resolved
 * rgb values are repeated here for each colour scheme.
 */
const GUIDE_COLOURS: Record<'dark' | 'light', Record<VoiceName, string>> = {
  dark: {
    tonic: 'rgba(240,71,112,0.10)',
    fourth: 'rgba(255,107,138,0.07)',
    fifth: 'rgba(69,227,239,0.08)',
  },
  light: {
    tonic: 'rgba(200,56,96,0.11)',
    fourth: 'rgba(214,80,118,0.08)',
    fifth: 'rgba(19,182,194,0.09)',
  },
}

/* Soft radial glow sprite, one per voice colour */
function makeSprite(r: number, g: number, b: number): HTMLCanvasElement {
  const sprite = document.createElement('canvas')
  const radius = 32
  sprite.width = sprite.height = radius * 2
  const c = sprite.getContext('2d')!
  const grad = c.createRadialGradient(radius, radius, 0, radius, radius, radius)
  grad.addColorStop(0, `rgba(${r},${g},${b},1)`)
  grad.addColorStop(0.35, `rgba(${r},${g},${b},0.45)`)
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`)
  c.fillStyle = grad
  c.fillRect(0, 0, radius * 2, radius * 2)
  return sprite
}

interface WavelineOptions {
  /**
   * Draw with this colour scheme's palette instead of following
   * the system preference. Keep in step with any forced
   * `color-scheme` on the hero wrapper.
   */
  colorScheme?: 'light' | 'dark'
}

/**
 * Start the waveline animation on the given canvas.
 *
 * Pointer interaction listens on `host` (the hero section) so the
 * glow follows the pointer across the whole hero, not just the
 * canvas. Returns a cleanup function that stops the animation and
 * removes all listeners.
 */
export function startWaveline(
  canvas: HTMLCanvasElement,
  host: HTMLElement,
  options: WavelineOptions = {},
): () => void {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}

  const isDark = () =>
    options.colorScheme
      ? options.colorScheme === 'dark'
      : matchMedia('(prefers-color-scheme: dark)').matches

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches

  const sprites = {
    dark: {
      tonic: makeSprite(240, 71, 112),
      fourth: makeSprite(255, 107, 138),
      fifth: makeSprite(69, 227, 239),
    },
    light: {
      tonic: makeSprite(200, 56, 96),
      fourth: makeSprite(214, 80, 118),
      fifth: makeSprite(19, 182, 194),
    },
  }

  let width = 0
  let height = 0

  function resize() {
    const dpr = Math.min(devicePixelRatio || 1, 2)
    width = canvas.clientWidth
    height = canvas.clientHeight
    canvas.width = width * dpr
    canvas.height = height * dpr
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  function voiceY(voice: Voice, x: number, t: number): number {
    const phase =
      (x / width) * CYCLES * voice.ratio * Math.PI * 2 +
      voice.dir * (t / T_TONIC) * voice.ratio * Math.PI * 2
    return (CENTRE + Math.sin(phase) * voice.amp) * height
  }

  let particles: Particle[] = []

  function seed() {
    particles = []
    const perVoice = Math.round(Math.min(width / 5.5, 260))
    for (const voice of VOICES) {
      for (let i = 0; i < perVoice; i++) {
        particles.push({
          voice,
          x: ((i + 0.5) / perVoice) * width,
          size: 0.9 + Math.random() * 1.5,
          twinklePhase: Math.random() * Math.PI * 2,
        })
      }
    }
  }

  const pointer = { x: -9999, energy: 0 }

  function onPointerMove(e: PointerEvent) {
    const rect = canvas.getBoundingClientRect()
    pointer.x = e.clientX - rect.left
    pointer.energy = Math.min(pointer.energy + 0.05, 1)
  }

  function onPointerLeave() {
    pointer.x = -9999
  }

  function draw(t: number) {
    ctx!.clearRect(0, 0, width, height)
    const dark = isDark()
    const scheme = dark ? 'dark' : 'light'
    ctx!.globalCompositeOperation = dark ? 'lighter' : 'source-over'
    pointer.energy *= 0.985

    /* faint guide curves */
    for (const voice of VOICES) {
      ctx!.beginPath()
      for (let x = 0; x <= width; x += 8) {
        const y = voiceY(voice, x, t)
        if (x === 0) ctx!.moveTo(x, y)
        else ctx!.lineTo(x, y)
      }
      ctx!.strokeStyle = GUIDE_COLOURS[scheme][voice.name]
      ctx!.lineWidth = 1
      ctx!.stroke()
    }

    for (const p of particles) {
      const y = voiceY(p.voice, p.x, t)

      /* unison glow: brighten where this voice meets another */
      let ring = 0
      for (const other of VOICES) {
        if (other === p.voice) continue
        const d = Math.abs(voiceY(other, p.x, t) - y)
        if (d < UNISON) ring = Math.max(ring, 1 - d / UNISON)
      }

      /* the pointer lends a little presence nearby */
      const dx = Math.abs(p.x - pointer.x)
      const near =
        dx < 200
          ? Math.exp(-(dx * dx) / (2 * 80 * 80)) * pointer.energy
          : 0

      const twinkle = 0.7 + 0.3 * Math.sin(t * 0.0011 + p.twinklePhase)
      const alpha =
        (dark ? 0.4 : 0.34) * p.voice.weight * twinkle +
        ring * (dark ? 0.35 : 0.25) +
        near * 0.15
      const size = p.size * (8 + ring * 5)
      ctx!.globalAlpha = Math.min(alpha, 0.85)
      ctx!.drawImage(
        sprites[scheme][p.voice.name],
        p.x - size / 2,
        y - size / 2,
        size,
        size,
      )
    }
    ctx!.globalAlpha = 1
  }

  /* With reduced motion, draw a single mid-cycle frame and stop */
  const STILL_FRAME_T = 5000

  resize()
  seed()

  let raf = 0

  const loop = (t: number) => {
    draw(t)
    raf = requestAnimationFrame(loop)
  }

  function onVisibilityChange() {
    if (document.hidden) cancelAnimationFrame(raf)
    else raf = requestAnimationFrame(loop)
  }

  const resizeObserver = new ResizeObserver(() => {
    resize()
    seed()
    if (reduceMotion) draw(STILL_FRAME_T)
  })
  resizeObserver.observe(canvas)

  if (reduceMotion) {
    draw(STILL_FRAME_T)
  } else {
    raf = requestAnimationFrame(loop)
    document.addEventListener('visibilitychange', onVisibilityChange)
    host.addEventListener('pointermove', onPointerMove)
    host.addEventListener('pointerleave', onPointerLeave)
  }

  return () => {
    cancelAnimationFrame(raf)
    resizeObserver.disconnect()
    document.removeEventListener('visibilitychange', onVisibilityChange)
    host.removeEventListener('pointermove', onPointerMove)
    host.removeEventListener('pointerleave', onPointerLeave)
  }
}
